// useLocalTimer — receives the `local_timer:set` SSE event emitted by
// the mirror-agent server tool of the same name (src/local_timer_tools.mjs)
// and routes it through one of two paths:
//
//   1. Running inside the Vulture Kitchen Electron app: hand the timer
//      off to `window.VultureKitchen.scheduleTimer(...)`. The Electron
//      main process schedules a Node setTimeout, plays sound on expiry,
//      shows a Windows toast, forces the display on. See
//      kitchen/electron/main.js for the receiving side.
//
//   2. Running in any other client (regular browser, mobile WebView,
//      desktop electron without the kitchen bridge): schedule the timer
//      ENTIRELY inside the renderer via a JS setTimeout, and play a
//      sound + toast on expiry through the renderer. This is the fallback
//      so phone users can still get a usable cook timer when they hit
//      the kitchen-style tool — they just lose the "wake the device,
//      foreground the window" treatment that only Electron can do.
//
// Either way, the timer is local to THIS device. The mirror-agent tool
// emits the SSE event only to the originating run's session, so other
// devices the same user is signed into never see it.

import { useEffect, useRef } from "preact/hooks";
import { sse } from "./sse";

// Native bridge — exposed by the kitchen Electron preload script.
// Synchronous-style API mirrors VultureMic / VultureTts. Methods return
// a string that's either "ok" or a JSON-encoded error message. The
// renderer doesn't need to await anything — the actual setTimeout
// runs inside the main process and survives even if the renderer is
// reloaded.
interface VultureKitchenBridge {
  /** Returns "ok" when the bridge is reachable from the renderer. */
  isAvailable: () => string;
  /** Schedule a local timer. `payload` is a JSON string of
   *  { localId, seconds, label, fireAtMs }. Returns "ok" on success. */
  scheduleTimer: (payload: string) => string;
  /** Force the display on and bring the window forward.
   *  Called by the renderer when the wake-word indicator should
   *  pull the screen out of blanking. Returns "ok" on success. */
  forceDisplayOn?: () => string;
}

declare global {
  interface Window {
    VultureKitchen?: VultureKitchenBridge;
  }
}

interface LocalTimerSetEvent {
  type: "local_timer:set";
  runId?: string;
  localId: string;
  seconds: number;
  label: string;
  fireAtMs: number;
}

export interface ActiveLocalTimer {
  localId: string;
  label: string;
  fireAtMs: number;
  /** Set when the timer is scheduled in the renderer (browser-mode
   *  fallback). null when the kitchen Electron bridge owns it (we
   *  don't track in the renderer in that case — main process is the
   *  source of truth). */
  jsTimeoutId: number | null;
}

interface UseLocalTimerArgs {
  /** Called when a timer is first scheduled (either via the kitchen
   *  bridge or the renderer fallback). Used for an in-line "timer set"
   *  confirmation card. */
  onScheduled?: (timer: ActiveLocalTimer) => void;
  /** Called when a renderer-scheduled (fallback path) timer fires. Not
   *  fired for kitchen-bridge timers — the main process handles those
   *  entirely; the renderer doesn't see the expiry event. */
  onFiredFallback?: (timer: ActiveLocalTimer) => void;
}

/** Subscribes to local_timer:set SSE events and routes them either to
 *  the kitchen Electron bridge (if present) or a renderer-side fallback.
 *  Mount this once at the App level — multiple mounts would double-
 *  schedule each incoming timer. */
export function useLocalTimer({ onScheduled, onFiredFallback }: UseLocalTimerArgs = {}) {
  const fallbackTimers = useRef<Map<string, ActiveLocalTimer>>(new Map());

  useEffect(() => {
    return sse.on((ev: any) => {
      if (ev?.type !== "local_timer:set") return;
      const e = ev as LocalTimerSetEvent;
      const seconds = Number(e.seconds);
      const label = String(e.label || "Timer");
      const localId = String(e.localId || `lt-fallback-${Date.now()}`);
      const fireAtMs = Number(e.fireAtMs) || (Date.now() + seconds * 1000);

      const bridge = typeof window !== "undefined" ? window.VultureKitchen : undefined;
      if (bridge?.scheduleTimer) {
        // Kitchen-Electron path. Hand the whole timer to the main
        // process and walk away. The native side owns the schedule,
        // the sound on expiry, the OS notification, and the window
        // foreground-on-fire.
        try {
          const payload = JSON.stringify({ localId, seconds, label, fireAtMs });
          const result = bridge.scheduleTimer(payload);
          if (result === "ok") {
            onScheduled?.({ localId, label, fireAtMs, jsTimeoutId: null });
            return;
          }
          console.warn("[local-timer] kitchen bridge returned non-ok:", result);
        } catch (err) {
          console.warn("[local-timer] kitchen bridge threw:", (err as Error)?.message || err);
        }
        // Fall through to the renderer fallback if the bridge errored.
      }

      // Renderer fallback. Schedule a JS setTimeout, play a built-in
      // sound on expiry (the same chime asset used elsewhere), and
      // fire a Notification if permission is already granted.
      const delayMs = Math.max(0, fireAtMs - Date.now());
      const jsTimeoutId = window.setTimeout(() => {
        const timer = fallbackTimers.current.get(localId);
        if (!timer) return;
        fallbackTimers.current.delete(localId);
        // Play a quick attention-getting beep. Re-use the existing
        // listening-tone chime if available; otherwise just a
        // synthesized one.
        try {
          const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.frequency.value = 880;
          gain.gain.value = 0.2;
          osc.connect(gain).connect(ctx.destination);
          osc.start();
          setTimeout(() => { osc.stop(); ctx.close(); }, 800);
        } catch { /* audio not allowed before user gesture; fine */ }
        // Optional system notification if permission is already there.
        try {
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            new Notification(`${timer.label} done`, { body: "Local timer finished." });
          }
        } catch { /* ignore */ }
        onFiredFallback?.(timer);
      }, delayMs);

      const timer: ActiveLocalTimer = { localId, label, fireAtMs, jsTimeoutId };
      fallbackTimers.current.set(localId, timer);
      onScheduled?.(timer);
    });
    // sse.on returns the unsubscriber; cleanup also clears any pending
    // fallback timeouts so a remount doesn't leave orphan setTimeouts.
  }, [onScheduled, onFiredFallback]);

  // Cleanup all in-flight fallback timeouts on unmount. Kitchen-bridge
  // timers are owned by the main process; they're unaffected.
  useEffect(() => {
    const map = fallbackTimers.current;
    return () => {
      for (const t of map.values()) {
        if (t.jsTimeoutId != null) window.clearTimeout(t.jsTimeoutId);
      }
      map.clear();
    };
  }, []);
}
